import { useCallback, useEffect, useRef, useState } from "react";

export type SaveState = "saved" | "saving" | "unsaved" | "error";

interface UseAutosaveOptions {
  /** Persist the given content. Resolve on success, reject on failure. */
  save: (content: string, reason: "autosave" | "manual") => Promise<void>;
  intervalMs: number;
}

interface AutosaveApi {
  state: SaveState;
  /** Register new content and (re)arm the debounce timer. */
  schedule: (content: string) => void;
  /** Persist immediately (Ctrl+S, blur, scene switch). */
  saveNow: (reason?: "autosave" | "manual") => Promise<void>;
  /** True while there is unsaved content. */
  hasPending: boolean;
}

/**
 * Quiet, non-blocking autosave. Typing never waits on the network: the latest
 * content is held in a ref and flushed on a debounce, on demand, and on
 * teardown so no keystroke is lost when switching scenes. The manuscript is
 * only ever persisted exactly as provided — nothing here rewrites content.
 */
export function useAutosave({ save, intervalMs }: UseAutosaveOptions): AutosaveApi {
  const [state, setState] = useState<SaveState>("saved");
  const [hasPending, setHasPending] = useState(false);

  const pendingContent = useRef<string | null>(null);
  const savingContent = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const flush = useCallback(async (reason: "autosave" | "manual") => {
    clearTimer();
    const content = pendingContent.current;
    if (content === null) {
      return;
    }
    pendingContent.current = null;
    savingContent.current = content;
    setState("saving");
    try {
      await saveRef.current(content, reason);
      // If more was typed while saving, we remain pending; otherwise saved.
      if (pendingContent.current === null) {
        setState("saved");
        setHasPending(false);
      } else {
        setState("unsaved");
      }
    } catch {
      // Put the content back so a later attempt (or manual save) retries it,
      // unless newer content already superseded it.
      if (pendingContent.current === null) {
        pendingContent.current = content;
      }
      setState("error");
      setHasPending(true);
    } finally {
      savingContent.current = null;
    }
  }, []);

  const schedule = useCallback(
    (content: string) => {
      pendingContent.current = content;
      setHasPending(true);
      setState((s) => (s === "saving" ? s : "unsaved"));
      clearTimer();
      timer.current = window.setTimeout(() => {
        void flush("autosave");
      }, intervalMs);
    },
    [flush, intervalMs]
  );

  const saveNow = useCallback((reason: "autosave" | "manual" = "manual") => flush(reason), [flush]);

  // Flush on teardown (scene switch / unmount) so nothing is lost.
  useEffect(() => {
    return () => {
      clearTimer();
      if (pendingContent.current !== null) {
        // Fire-and-forget; the component is going away.
        void saveRef.current(pendingContent.current, "autosave");
        pendingContent.current = null;
      }
    };
  }, []);

  // Best-effort save if the tab is being hidden or closed.
  useEffect(() => {
    const onHide = () => {
      if (pendingContent.current !== null) {
        void flush("autosave");
      }
    };
    window.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, [flush]);

  return { state, schedule, saveNow, hasPending };
}
